import React, { useState, useEffect, useRef } from 'react';

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceDetails {
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  address_components?: google.maps.GeocoderAddressComponent[];
}

interface CustomAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: PlaceDetails) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function CustomAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  className,
  style,
}: CustomAutocompleteProps) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [justSelected, setJustSelected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const justSelectedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );

  // Initialize Google Places services
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();

      // Create a dummy div for PlacesService (it requires a map or div)
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);

      console.log('🗺️ Google Places services initialized');
    }
  }, []);

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Update ref whenever justSelected changes
  useEffect(() => {
    justSelectedRef.current = justSelected;
  }, [justSelected]);

  // Handle input changes and fetch predictions
  useEffect(() => {
    const fetchPredictions = async () => {
      console.log('🔄 fetchPredictions called', { value, justSelectedRef: justSelectedRef.current, isInitialized });
      
      if (!value.trim() || value.length < 3) {
        console.log('🚫 Value too short or empty', { value, length: value.length });
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      // Don't fetch predictions if we just selected a place
      if (justSelectedRef.current) {
        console.log('🚫 SKIPPING - justSelectedRef is TRUE', { justSelectedRef: justSelectedRef.current });
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      // Don't auto-open on initial load with existing values
      if (!isInitialized) {
        console.log(
          '🚫 Skipping prediction fetch - component not initialized yet'
        );
        return;
      }

      if (!autocompleteServiceRef.current) {
        console.warn('⚠️ AutocompleteService not available');
        return;
      }

      console.log('📡 FETCHING predictions for:', { value });
      setIsLoading(true);

      try {
        const request = {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' }, // Restrict to US addresses
        };

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (predictions, status) => {
            console.log('🔍 Predictions response:', { predictions: predictions?.length || 0, status, justSelectedRef: justSelectedRef.current });

            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              console.log('✅ Setting predictions:', { count: predictions.length });
              setPredictions(predictions);
              setIsOpen(true);
            } else {
              console.log('❌ No predictions or bad status');
              setPredictions([]);
              setIsOpen(false);
            }
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('❌ Error fetching predictions:', error);
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPredictions, 300); // Debounce
    return () => {
      clearTimeout(timeoutId);
      console.log('🧹 Cleaned up timeout');
    };
  }, [value, isInitialized]);

  // Handle prediction selection
  const handlePredictionSelect = async (prediction: PlacePrediction) => {
    console.log('📍 Prediction selected:', prediction.description);

    if (!placesServiceRef.current) {
      console.warn('⚠️ PlacesService not available');
      return;
    }

    console.log('🔒 SETTING justSelected to TRUE');
    setIsLoading(true);
    setIsOpen(false);
    setJustSelected(true); // Mark that we just selected a place
    setPredictions([]);

    try {
      const request = {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'address_components'],
      };

      placesServiceRef.current.getDetails(request, (place, status) => {
        console.log('📍 Place details response:', { address: place?.formatted_address, status });

        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const placeDetails: PlaceDetails = {
            formatted_address: place.formatted_address || '',
            geometry: {
              location: {
                lat: place.geometry?.location?.lat() || 0,
                lng: place.geometry?.location?.lng() || 0,
              },
            },
            address_components: place.address_components || [],
          };

          console.log('📍 Calling onPlaceSelected with:', placeDetails.formatted_address);
          onChange(placeDetails.formatted_address);
          onPlaceSelected(placeDetails);

          // Reset the justSelected flag after a longer delay to prevent re-fetching
          console.log('⏰ Setting timeout to reset justSelected after 1000ms');
          setTimeout(() => {
            console.log('🔓 RESETTING justSelected to FALSE');
            setJustSelected(false);
            console.log('✅ Reset justSelected flag');
          }, 1000);
        } else {
          console.error('❌ Error getting place details:', status);
          setJustSelected(false);
        }
        setIsLoading(false);
      });
    } catch (error) {
      console.error('❌ Error getting place details:', error);
      setJustSelected(false);
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('⌨️ Input changed to:', newValue, { justSelected });

    // Reset justSelected flag when user manually types
    if (justSelected) {
      console.log('🔄 User typing while justSelected=true - resetting');
      setJustSelected(false);
      // Close predictions since user is modifying the value
      setPredictions([]);
      setIsOpen(false);
    }

    onChange(newValue);
  };

  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent form submission on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        style={{
          backgroundColor: '#FFFFFF',
          borderColor: '#D1D5DB',
          color: '#1F2937',
          ...(className ? {} : { width: '100%', padding: '8px 12px', border: '1px solid', borderRadius: '6px' }),
          ...style,
        }}
      />

      {isOpen && predictions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          marginTop: '4px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #E5E7EB',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Prediction clicked:', prediction.description);
                handlePredictionSelect(prediction);
              }}
              onMouseDown={(e) => {
                // Prevent form event bubbling
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
              }}
            >
              <div style={{ fontSize: '14px' }}>
                <div style={{ fontWeight: '500', color: '#1F2937' }}>
                  {prediction.structured_formatting.main_text}
                </div>
                <div style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                  {prediction.structured_formatting.secondary_text}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          marginTop: '4px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            padding: '12px 16px',
            fontSize: '14px',
            color: '#6B7280',
            textAlign: 'center'
          }}>
            Loading suggestions...
          </div>
        </div>
      )}
    </div>
  );
}

